import { theme } from '#app/theme/theme.js';
import { type SkeletonLoaderProps } from './types.js';
import { forwardRef, type Ref, useMemo } from 'react';
import { styled } from 'styled-components';

const uid = (): string => Math.random().toString(36).slice(6);

const SkeletonLoaderWrapper = styled.div`
  overflow: hidden;
  width: 100%;
`;

const DefaultItemWrapper = styled.div`
  margin: 24px 0px;
  overflow: hidden;
`;

export const SkeletonLoader = forwardRef(
  (
    {
      animate = true,
      baseUrl = '',
      children,
      colorBase = theme.colorsV2.gray30,
      delayIncrement = 0.2,
      gradientColor1 = theme.colorsV2.gray50,
      gradientColor2 = theme.colorsV2.gray70,
      gradientColorOpacity1 = 0.8,
      gradientColorOpacity2 = 0.8,
      gradientRatio = 1.5,
      interval = 0.25,
      isLoading = false,
      ItemWrapper = DefaultItemWrapper,
      repeat = 1,
      rtl = false,
      speed = 2.8,
      svgStyle = {},
      title = 'Loading...',
      uniqueKey,
      ...props
    }: SkeletonLoaderProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const fixedId = uniqueKey ?? uid();
    const svgRtlStyle = rtl ? { transform: 'scaleX(-1)' } : null;
    const keyTimes = `0; ${interval}; 1`;
    const dur = `${speed}s`;

    const loaders = useMemo(
      () =>
        Array.from({ length: repeat }).map((_, index) => ({
          ariaId: `${fixedId}-${index}-aria`,
          clipId: `${fixedId}-${index}-diff`,
          delay: 0 + index * delayIncrement,
          gradientId: `${fixedId}-${index}-animated-diff`,
          idx: `${fixedId}-${index}`,
        })),
      [delayIncrement, fixedId, repeat],
    );

    return (
      <SkeletonLoaderWrapper ref={ref}>
        {isLoading &&
          loaders.map(({ clipId, delay, gradientId, idx }) => (
            <ItemWrapper key={idx}>
              <svg
                aria-labelledby={`${idx}-aria`}
                role="img"
                style={{ ...svgStyle, ...svgRtlStyle }}
                {...props}
              >
                {title ? <title id={`${idx}-aria`}>{title}</title> : null}

                <rect
                  clipPath={`url(${baseUrl}#${clipId})`}
                  height="100%"
                  role="presentation"
                  style={{ fill: colorBase }}
                  width="100%"
                  x="0"
                  y="0"
                />

                <rect
                  clipPath={`url(${baseUrl}#${clipId})`}
                  height="100%"
                  role="presentation"
                  style={{ fill: `url(${baseUrl}#${gradientId})` }}
                  width="100%"
                  x="0"
                  y="0"
                />

                <defs>
                  <clipPath id={clipId}>{children}</clipPath>
                  <linearGradient id={gradientId}>
                    <stop
                      offset="0%"
                      stopColor={gradientColor1}
                      stopOpacity={gradientColorOpacity1}
                    >
                      {animate && (
                        <animate
                          attributeName="offset"
                          begin={delay}
                          dur={dur}
                          keyTimes={keyTimes}
                          repeatCount="indefinite"
                          values={`${-gradientRatio}; ${-gradientRatio}; 1`}
                        />
                      )}
                    </stop>

                    <stop
                      offset="50%"
                      stopColor={gradientColor2}
                      stopOpacity={gradientColorOpacity2}
                    >
                      {animate && (
                        <animate
                          attributeName="offset"
                          begin={delay}
                          dur={dur}
                          keyTimes={keyTimes}
                          repeatCount="indefinite"
                          values={`${-gradientRatio / 2}; ${
                            -gradientRatio / 2
                          }; ${1 + gradientRatio / 2}`}
                        />
                      )}
                    </stop>

                    <stop
                      offset="100%"
                      stopColor={gradientColor1}
                      stopOpacity={gradientColorOpacity1}
                    >
                      {animate && (
                        <animate
                          attributeName="offset"
                          begin={delay}
                          dur={dur}
                          keyTimes={keyTimes}
                          repeatCount="indefinite"
                          values={`0; 0; ${1 + Number(gradientRatio)}`}
                        />
                      )}
                    </stop>
                  </linearGradient>
                </defs>
              </svg>
            </ItemWrapper>
          ))}
      </SkeletonLoaderWrapper>
    );
  },
);
